import { AnchorButton, Checkbox, Icon, InputGroup } from '@mc/react-core';
import classNames from 'classnames';
import React from 'react';
import { useTheme } from '../useTheme';

const defgetId = (i: any) => i?.id;
const getLabel = (i: any) => i.label;

const DEFAULT_EMPTY = {
  id: null,
  label: 'Prázdná hodnota',
};

type RenderItemMeth<T> = React.FC<{
  item: T;
  selected: boolean;
  onSelect: () => void;
  className?: string;
  active: boolean;
}>;

type Props<ID, ITEM> = {
  items: ITEM[];
  onChange: (val: ID[]) => void;
  value: ID[];
  className?: string;
  renderItem?: RenderItemMeth<ITEM>;
  getSearchableFn?: (i: ITEM) => string;
  getId?: (i: ITEM) => ID;
  canBeEmpty?: boolean | ITEM;
};

export function MultiSelectSearch<ID, ITEM>({
  items,
  onChange,
  value = [],
  className,
  canBeEmpty = false,
  renderItem = ({ item, selected, active, onSelect }) => {
    return (
      <div
        className={classNames('item', { 'item-active': active })}
        key={(item as any).id}
        style={active ? { backgroundColor: 'rgba(0,0,0,0.2)' } : {}}
      >
        <Checkbox
          onChange={onSelect}
          checked={selected}
          className={'mb-0 ' + ((item as any).className || '')}
          tabIndex={-1}
        >
          {(item as any).label}
        </Checkbox>
      </div>
    );
  },
  getSearchableFn = getLabel,
  getId = defgetId,
}: Props<ID, ITEM>) {
  const theme = useTheme();
  const [search, setSearch] = React.useState('');
  const [focus, setFocus] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  let srchVal = search.toLowerCase();
  const fixedVal = value !== undefined ? value : [];

  let realItems = items;
  if (srchVal && srchVal.length > 0) {
    realItems = realItems.filter((i) => {
      const searchIn: string = getSearchableFn(i);
      return (
        searchIn &&
        searchIn.length > 0 &&
        (searchIn.toLowerCase().indexOf(srchVal) !== -1 ||
          fixedVal.indexOf(getId(i)) !== -1)
      );
    });
  } else if (canBeEmpty) {
    if (canBeEmpty === true) {
      realItems = [DEFAULT_EMPTY as any as ITEM, ...realItems];
    } else {
      realItems = [canBeEmpty, ...realItems];
    }
  }

  const keyHand = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key == 'ArrowUp') {
        setActiveIndex((act) => (act - 1 >= 0 ? act - 1 : 0));
      } else if (e.key === 'ArrowDown') {
        setActiveIndex((act) =>
          act + 1 <= realItems.length - 1 ? act + 1 : realItems.length - 1
        );
      } else if (e.key === 'PageUp') {
        if (e.altKey) {
        } else {
          setActiveIndex((act) => (act - 10 >= 0 ? act - 10 : 0));
        }
      } else if (e.key === 'PageDown') {
        if (e.altKey) {
          setActiveIndex((act) =>
            act + 10 <= realItems.length - 10 ? act + 10 : realItems.length - 1
          );
        } else {
          setActiveIndex((act) =>
            act + 10 <= realItems.length - 10 ? act + 10 : realItems.length - 1
          );
        }
      } else if (e.key === 'Enter') {
        setActiveIndex((activeIndex) => {
          const item = realItems[activeIndex];
          const id = getId(item);
          const index = fixedVal.indexOf(id);
          if (id) {
            onChange(
              index !== -1
                ? [...fixedVal.slice(0, index), ...fixedVal.slice(index + 1)]
                : [...fixedVal, id]
            );
          }
          return activeIndex;
        });
      }
    },
    [realItems, fixedVal]
  );

  const selectAll = React.useCallback(() => {
    onChange(items.map((i) => getId(i)));
  }, [items]);
  const unSellectAll = React.useCallback(() => {
    onChange([]);
  }, []);

  return (
    <div className={classNames(`${theme.ns}-multi-select-search`, className)}>
      <InputGroup
        autoFocus
        placeholder="vyhledat"
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setSearch(e.target.value);
        }}
        rightElement={<Icon icon="search" className="mt-1 mr-1" />}
        onKeyDown={keyHand}
        className={'multi-select-search-search-input'}
      />
      <div className={'multi-select-search-buttons'}>
        <AnchorButton
          fill
          small
          onClick={selectAll}
          disabled={fixedVal.length === items.length}
        >
          Vybrat vše
        </AnchorButton>
        <AnchorButton
          fill
          small
          onClick={unSellectAll}
          disabled={fixedVal.length < 1}
        >
          Odebrat vše
        </AnchorButton>
      </div>
      <div className="multi-select-search-item-container">
        {realItems.length === 0 && <div className={'item text-center'}>Žádné položky</div>}
        {realItems.map((item, listIndex) => {
          const id = getId(item);
          const index = fixedVal.indexOf(id);
          const selected = index !== -1;
          const onSelect = selected
            ? () =>
                onChange([
                  ...fixedVal.slice(0, index),
                  ...fixedVal.slice(index + 1),
                ])
            : () => onChange([...fixedVal, id]);
          return renderItem({
            item,
            selected,
            onSelect,
            active: focus && activeIndex === listIndex,
          });
        })}
      </div>
    </div>
  );
}
